import React from 'react';
import { X, AlertTriangle, CheckCircle } from 'lucide-react';
import { useSyncStore } from '../lib/offline/sync-store';
import { ConflictEntry } from '../lib/offline/db';
import { isPayLockRefusal } from '../lib/pay-lock';

function getEntityLabel(entry: ConflictEntry): string {
  const entity = entry.entity;
  const name = entry.serverRecord?.name
    || entry.serverRecord?.ticket_number
    || entry.serverRecord?.job_number
    || entry.serverRecord?.customer_name
    || entry.id.slice(0, 8);
  return `${entity.replace(/s$/, '')} ${name}`;
}

/**
 * WHAT THE SERVER HOLDS FOR ONE OF THE QUEUED FIELDS.
 *
 * A queued ticket edit is camelCase (`workDate`, `rigName`): the shape the
 * write door takes. The server record that comes back on a conflict is the
 * snake_case row (`work_date`, `rig_name`). Reading `server[key]` with the
 * camelCase key found nothing, so every field printed "→ null". That said the
 * server had emptied the ticket when it had changed nothing. Measured on Smith,
 * FT-00077.
 *
 * So both spellings are looked up. A field the server record does not carry
 * under either name is NOT compared. Its absence says nothing about the server's
 * value, so it is listed as "not in the server's copy", never shown as empty.
 * A field the server does carry with no value IS empty, and is shown as such.
 *
 * Columns that carry a digit split it off (`assigned_tech_1_id`,
 * `tax_rate_1_label`), so `assignedTech1Id` is tried as both
 * `assigned_tech1_id` and `assigned_tech_1_id`. A key that matches neither
 * fails safe: it is listed as not compared.
 */
const toSnake = (key: string) => key.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
const toSnakeDigits = (key: string) => toSnake(key).replace(/([a-z])(\d)/g, '$1_$2');

// Bookkeeping, not fields. `version`, `updated_at` and `updatedAt` are how a
// conflict is detected. `knownLineItemIds`, `discountAware` and `draftId` are
// request flags the ticket write door reads (which rows the device was handed,
// whether it understands discounts, which AI draft it came from). None of
// them is anything the hand typed.
const SKIPPED_KEYS = new Set(['version', 'updated_at', 'updatedAt', 'knownLineItemIds', 'discountAware', 'draftId']);

type Present = { found: true; value: unknown } | { found: false };

function serverValue(server: Record<string, any>, key: string): Present {
  if (server && Object.prototype.hasOwnProperty.call(server, key)) return { found: true, value: server[key] };
  for (const snake of [toSnake(key), toSnakeDigits(key)]) {
    if (server && snake !== key && Object.prototype.hasOwnProperty.call(server, snake)) {
      return { found: true, value: server[snake] };
    }
  }
  return { found: false };
}

// An empty list and no list are the same thing to the reader: nothing there.
const isEmpty = (v: unknown) => v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0);

/** JSON with object keys sorted. jsonb re-orders keys, so key order is not a difference. */
function stableJson(v: unknown): string {
  if (Array.isArray(v)) return `[${v.map(stableJson).join(',')}]`;
  if (v && typeof v === 'object') {
    return `{${Object.keys(v as object).sort().map((k) => `${JSON.stringify(k)}:${stableJson((v as any)[k])}`).join(',')}}`;
  }
  return JSON.stringify(v);
}

/**
 * A plain decimal, written canonically: `4870.00` → `4870`, `1.30` → `1.3`.
 * It is a string rewrite, not `Number()`. `Number()` would make `0512345` equal
 * `512345`, `1e3` equal `1000`, and two 17-digit ids equal each other, and a
 * well licence or AFE number is exactly the free text that can differ that way.
 * A leading zero is kept, so `0512345` stays different from `512345`.
 */
function canonicalDecimal(s: string): string | null {
  const m = s.match(/^(-?)(0|[1-9]\d*)(?:\.(\d+))?$/);
  if (!m) return null;
  const frac = (m[3] || '').replace(/0+$/, '');
  return `${m[1]}${m[2]}${frac ? `.${frac}` : ''}`;
}

/**
 * Equal as the reader would judge it. A measure arrives as `"4870.00"` from one
 * side and `4870` from the other, and a time as `07:00` or `07:00:00`. Neither is
 * a difference anyone could act on.
 */
function sameValue(a: unknown, b: unknown): boolean {
  if (isEmpty(a) && isEmpty(b)) return true;
  if (isEmpty(a) || isEmpty(b)) return false;
  if (typeof a === 'object' || typeof b === 'object') return stableJson(a) === stableJson(b);
  const sa = String(a);
  const sb = String(b);
  if (sa === sb) return true;
  const da = canonicalDecimal(sa);
  const db = canonicalDecimal(sb);
  if (da !== null && db !== null) return da === db;
  const time = (t: string) => (/^\d{2}:\d{2}$/.test(t) ? `${t}:00` : t);
  return time(sa) === time(sb);
}

export interface FieldDiffs {
  diffs: { key: string; local: unknown; server: unknown }[];
  /** Queued fields the server record does not carry, so they cannot be compared. */
  notCompared: string[];
}

export function getDiffFields(local: Record<string, any> | undefined, server: Record<string, any> | undefined): FieldDiffs {
  const diffs: FieldDiffs['diffs'] = [];
  const notCompared: string[] = [];
  for (const key of Object.keys(local || {})) {
    if (key.startsWith('_') || SKIPPED_KEYS.has(key)) continue;
    const s = serverValue(server || {}, key);
    if (!s.found) { notCompared.push(key); continue; }
    if (!sameValue(local![key], s.value)) diffs.push({ key, local: local![key], server: s.value });
  }
  return { diffs, notCompared };
}

/**
 * One side of a difference. Empty reads as "empty", and a record is printed,
 * never "[object Object]". A list is summarised by its count. Two lists of the
 * same length that differ say "changed", or the row would claim a difference
 * while showing "2 items → 2 items".
 */
function DiffValue({ value, other, className }: { value: unknown; other?: unknown; className: string }) {
  if (isEmpty(value)) return <span className="italic text-fieldiq-muted">empty</span>;
  if (Array.isArray(value)) {
    const count = value.length === 1 ? '1 item' : `${value.length} items`;
    const changed = Array.isArray(other) && other.length === value.length;
    return <span className={className}>{changed ? `${count}, changed` : count}</span>;
  }
  if (typeof value === 'object') return <span className={className}>{JSON.stringify(value)}</span>;
  return <span className={className}>{String(value)}</span>;
}

interface Props {
  onClose: () => void;
}

const ConflictReviewPanel: React.FC<Props> = ({ onClose }) => {
  const { conflictDetails, dismissConflict, dismissAllConflicts } = useSyncStore();

  if (conflictDetails.length === 0) {
    return null;
  }
  // An approved-pay refusal or a paid-pay notice (api#1352). The server's
  // sentence is the whole explanation, so it is shown as the entry's text and
  // not as a muted aside, and neither shows the field diff. A notice has none,
  // because its edit DID save. A refused lock is refused for what it is (a
  // change to approved pay), not for which fields differ from the server's
  // copy, so a diff under it would answer a question nobody asked.
  const isNotice = (e: ConflictEntry) => e.kind === 'warning';
  const isPayLock = (e: ConflictEntry) => isPayLockRefusal({ code: e.code });
  const anyConflict = conflictDetails.some(e => !isNotice(e));

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div
        className="bg-fieldiq-card border border-fieldiq-border rounded-t-xl sm:rounded-xl w-full max-w-lg max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-fieldiq-border sticky top-0 bg-fieldiq-card z-10">
          <div className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-red-400" />
            <h2 className="text-base font-bold text-white">
              {anyConflict ? 'Sync Conflicts' : 'Sync Notices'} ({conflictDetails.length})
            </h2>
          </div>
          <div className="flex items-center gap-2">
            {conflictDetails.length > 1 && (
              <button
                onClick={() => dismissAllConflicts()}
                className="text-xs text-fieldiq-accent hover:text-amber-300 font-medium"
              >
                Dismiss All
              </button>
            )}
            <button onClick={onClose} className="text-fieldiq-muted hover:text-white">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="p-4 space-y-3">
          {anyConflict && (
            <p className="text-xs text-fieldiq-text">
              {conflictDetails.some(isNotice)
                ? 'Changes marked “Not saved” or with a conflict did not save. The server’s copy replaced those local edits.'
                : 'These changes did not save. The server’s copy replaced your local edits.'}
            </p>
          )}

          {conflictDetails.map((entry) => {
            const { diffs, notCompared } = getDiffFields(entry.localData, entry.serverRecord);
            return (
              <div
                key={entry.clientMutationId}
                className="bg-fieldiq-dark rounded-lg border border-fieldiq-border p-3"
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="text-sm font-medium text-white capitalize">
                      {getEntityLabel(entry)}
                    </p>
                    <p className="text-[10px] text-fieldiq-muted">
                      {new Date(entry.timestamp).toLocaleString()}
                    </p>
                  </div>
                  <button
                    onClick={() => dismissConflict(entry.clientMutationId)}
                    className="text-xs bg-fieldiq-card border border-fieldiq-border text-fieldiq-text hover:text-white px-2 py-1 rounded flex items-center gap-1"
                  >
                    <CheckCircle size={12} /> Dismiss
                  </button>
                </div>

                {entry.reason && (isNotice(entry) || isPayLock(entry)) ? (
                  <p className="text-xs text-fieldiq-text mb-2" data-testid="sync-entry-reason">
                    <span className="font-semibold">{isNotice(entry) ? 'Saved: ' : 'Not saved: '}</span>
                    {entry.reason}
                  </p>
                ) : entry.reason ? (
                  <p className="text-xs text-fieldiq-muted mb-2">{entry.reason}</p>
                ) : null}

                {isNotice(entry) || isPayLock(entry) ? null : (
                  <>
                    {diffs.length > 0 ? (
                      <div className="space-y-1" data-testid="sync-entry-diffs">
                        {diffs.map((d) => (
                          <div key={d.key} className="text-xs">
                            <span className="text-fieldiq-muted">{d.key}:</span>{' '}
                            <DiffValue value={d.local} other={d.server} className="text-red-400 line-through" />
                            {' → '}
                            <DiffValue value={d.server} other={d.local} className="text-green-400" />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-fieldiq-muted italic">
                        {notCompared.length > 0 ? 'No differences in the fields that could be compared' : 'No visible field differences'}
                      </p>
                    )}
                    {notCompared.length > 0 && (
                      <p className="text-xs text-fieldiq-muted mt-1" data-testid="sync-entry-not-compared">
                        Not in the server&rsquo;s copy, so not compared: {notCompared.join(', ')}
                      </p>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ConflictReviewPanel;
